// <save-button> Web Component
//
// Phase 1 of CDN caching refactor. Replaces today's two distinct save-heart
// surfaces (home cards' toggleHomeCardSave + property page's toggleSaveProperty)
// with one element that:
//   - reads localStorage savedProperties immediately (no flicker for already-saved)
//   - awaits window.userMenuReady, then merges in /api/favorites if authenticated
//   - on click: anonymous → opens login modal; authenticated → POST/DELETE to
//     /api/favorites/{id} with the dual-write to localStorage that main.js does
//
// Usage:
//   <save-button property-id="123" property-name="Hotel Foo"
//                property-image="https://..." property-address="Paris, France"
//                property-url="/property/hotel-foo-123">
//     <svg class="heart-empty">...</svg>
//   </save-button>

(function () {
    // Filled state: red, with fill applied for the heart shape.
    const FILLED_CLASSES = ['text-red-500', 'fill-current'];
    // Empty state: gray. We want to neutralize whatever gray Tailwind class
    // the SSR/markup originally used (gray-400 on home cards, gray-500 on
    // property page) so toggling never accumulates ".text-gray-500
    // text-red-500" with both winning by Tailwind cascade order.
    const ALL_GRAY_CLASSES = ['text-gray-300', 'text-gray-400', 'text-gray-500', 'text-gray-600', 'text-gray-700'];
    const DEFAULT_EMPTY_CLASS = 'text-gray-500';

    // sessionStorage key for /api/favorites snapshot (5min TTL).
    const FAV_CACHE_KEY = 'user_favorites_v1';
    const FAV_TTL_MS = 5 * 60 * 1000;

    let _favInFlight = null;

    function _readFavCache() {
        try {
            const raw = sessionStorage.getItem(FAV_CACHE_KEY);
            if (!raw) return null;
            const obj = JSON.parse(raw);
            if (!obj || (Date.now() - obj.t) > FAV_TTL_MS) {
                sessionStorage.removeItem(FAV_CACHE_KEY);
                return null;
            }
            return obj.ids || [];
        } catch (e) { return null; }
    }

    function _writeFavCache(ids) {
        try {
            sessionStorage.setItem(FAV_CACHE_KEY, JSON.stringify({ t: Date.now(), ids: ids }));
        } catch (e) { /* ignore */ }
    }

    // Returns Promise<{ids, definitive}>. `definitive=true` means the server
    // gave us a verified empty/non-empty list; `definitive=false` means we
    // couldn't determine (5xx / network) and callers MUST NOT use `ids` to
    // mutate local state — that path would silently delete a user's saved
    // hearts on a transient API error and the empty result would be cached
    // for the full TTL.
    function _fetchFavorites() {
        const cached = _readFavCache();
        if (cached) return Promise.resolve({ ids: cached, definitive: true });
        if (_favInFlight) return _favInFlight;
        _favInFlight = fetch('/api/favorites', { cache: 'no-store', credentials: 'same-origin' })
            .then(function (resp) {
                if (resp.status === 200) return resp.json().then(function (items) { return { items: items, definitive: true }; });
                if (resp.status === 401) return { items: [], definitive: true }; // anonymous = empty
                return { items: null, definitive: false }; // 5xx / other — indeterminate
            })
            .then(function (r) {
                if (!r.definitive) return { ids: [], definitive: false };
                const ids = (r.items || []).map(function (i) { return String(i.property_id); });
                _writeFavCache(ids); // only cache definitive outcomes
                return { ids: ids, definitive: true };
            })
            .catch(function () { return { ids: [], definitive: false }; }) // network: indeterminate
            .then(function (r) { _favInFlight = null; return r; });
        return _favInFlight;
    }

    // Invalidate favorites cache on auth change (login wipes it; cross-tab
    // logout already clears via main.js logout flow).
    document.addEventListener('user-changed', function () {
        try { sessionStorage.removeItem(FAV_CACHE_KEY); } catch (e) {}
    });

    class SaveButton extends HTMLElement {
        connectedCallback() {
            if (this._wiredOnce) return;
            this._wiredOnce = true;
            this._propertyId = this.getAttribute('property-id') || '';
            this._propertyName = this.getAttribute('property-name') || '';
            this._propertyImage = this.getAttribute('property-image') || '';
            this._propertyAddress = this.getAttribute('property-address') || '';
            this._propertyUrl = this.getAttribute('property-url') || '';
            // Click + auth-change listeners are safe to wire synchronously
            // (no child queries — only addEventListener on `this`/document).
            this._wireClick();
            // PR-perf.2: user-changed handled at module scope (see below —
            // replaces N per-instance listeners).
            // Per-element readyState-aware gate for _initialRender. It
            // calls _applyFilledState which does querySelector('svg') —
            // SSR SVG isn't in the DOM at upgrade-time (custom-element
            // reactions fire at opening tag, before children parse), so a
            // sync call returns null → heart stays gray for locally-saved
            // properties (memory/hydration_race_fixes_plan.md §3.B). Defer
            // until DCL when loading; run immediately for dynamic insertions.
            const self = this;
            const run = function () { self._initialRender(); };
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', run, { once: true });
            } else {
                run();
            }
        }

        // Synchronous first pass from localStorage so already-saved properties
        // show the filled heart immediately (no flicker before /api/favorites).
        _initialRender() {
            this._applyFilledState(this._isLocallySaved());
            // Then async: await window.userMenuReady, merge server state.
            const self = this;
            const ready = window.userMenuReady || Promise.resolve(null);
            ready.then(function () {
                self._mergeServerState();
            });
        }

        _isLocallySaved() {
            try {
                const raw = localStorage.getItem('bookingWorldOne_savedProperties');
                if (!raw) return false;
                const saved = JSON.parse(raw);
                return !!(saved && saved[this._propertyId]);
            } catch (e) { return false; }
        }

        _mergeServerState() {
            // Only meaningful for authenticated users.
            if (!window.currentUser) return;
            const self = this;
            _fetchFavorites().then(function (r) {
                // Indeterminate (5xx / network): leave localStorage and UI
                // alone. Otherwise a transient /api/favorites error would
                // silently empty the user's saved hearts on every page load
                // until the cache TTL expired.
                if (!r.definitive) return;
                const remoteSaved = r.ids.indexOf(self._propertyId) !== -1;
                const localSaved = self._isLocallySaved();
                // If remote disagrees, prefer remote and update local + UI.
                if (remoteSaved !== localSaved) {
                    try {
                        const raw = localStorage.getItem('bookingWorldOne_savedProperties');
                        const saved = raw ? JSON.parse(raw) : {};
                        if (remoteSaved) {
                            saved[self._propertyId] = {
                                name: self._propertyName,
                                image: self._propertyImage,
                                address: self._propertyAddress,
                                url: self._propertyUrl,
                                savedAt: new Date().toISOString(),
                            };
                        } else {
                            delete saved[self._propertyId];
                        }
                        localStorage.setItem('bookingWorldOne_savedProperties', JSON.stringify(saved));
                    } catch (e) { /* ignore */ }
                    self._applyFilledState(remoteSaved);
                }
            });
        }

        _applyFilledState(filled) {
            const svg = this.querySelector('svg');
            if (!svg) return;
            // Capture the original gray class on first invocation so we
            // restore the same shade SSR used (matches surface aesthetics).
            if (this._originalGrayClass === undefined) {
                this._originalGrayClass = '';
                for (let i = 0; i < ALL_GRAY_CLASSES.length; i++) {
                    if (svg.classList.contains(ALL_GRAY_CLASSES[i])) {
                        this._originalGrayClass = ALL_GRAY_CLASSES[i];
                        break;
                    }
                }
            }
            const grayClass = this._originalGrayClass || DEFAULT_EMPTY_CLASS;
            if (filled) {
                ALL_GRAY_CLASSES.forEach(function (c) { svg.classList.remove(c); });
                FILLED_CLASSES.forEach(function (c) { svg.classList.add(c); });
            } else {
                FILLED_CLASSES.forEach(function (c) { svg.classList.remove(c); });
                ALL_GRAY_CLASSES.forEach(function (c) {
                    if (c !== grayClass) svg.classList.remove(c);
                });
                svg.classList.add(grayClass);
            }
        }

        _wireClick() {
            const self = this;
            this.addEventListener('click', function (e) {
                e.preventDefault();
                e.stopPropagation();
                self._handleClick();
            });
        }

        async _handleClick() {
            // Mutation lock: rapid double-clicks shouldn't fire two
            // overlapping toggles. The first click owns the round trip;
            // subsequent clicks are ignored until it settles. Lock MUST
            // be set BEFORE any `await` so multiple clicks during the
            // pre-hydration window don't all queue past the guard and
            // race past the await — we'd otherwise see N overlapping
            // POST/DELETEs for N rapid clicks.
            if (this._inFlight) return;
            this._inFlight = true;

            try {
                await this._handleClickLocked();
            } finally {
                this._inFlight = false;
            }
        }

        async _handleClickLocked() {
            // If we haven't completed first hydration, block briefly until we have
            // an authoritative auth answer. This is the rare ~50-100ms window.
            if (window.currentUser === undefined) {
                try { await window.userMenuReady; } catch (e) { /* ignore */ }
            }

            if (!window.currentUser) {
                // Anonymous → trigger login modal
                document.dispatchEvent(new CustomEvent('open-signin', { bubbles: true }));
                return;
            }

            const id = this._propertyId;
            if (!id) return;

            const wasSaved = this._isLocallySaved();
            // Optimistic UI + localStorage update
            this._applyFilledState(!wasSaved);

            const writeLocal = function (saved) {
                try {
                    if (saved) {
                        const raw = localStorage.getItem('bookingWorldOne_savedProperties');
                        const map = raw ? JSON.parse(raw) : {};
                        map[id] = {
                            name: this._propertyName,
                            image: this._propertyImage,
                            address: this._propertyAddress,
                            url: this._propertyUrl,
                            savedAt: new Date().toISOString(),
                        };
                        localStorage.setItem('bookingWorldOne_savedProperties', JSON.stringify(map));
                    } else {
                        const raw = localStorage.getItem('bookingWorldOne_savedProperties');
                        const map = raw ? JSON.parse(raw) : {};
                        delete map[id];
                        localStorage.setItem('bookingWorldOne_savedProperties', JSON.stringify(map));
                    }
                } catch (e) { /* ignore */ }
            }.bind(this);

            writeLocal(!wasSaved);

            // Invalidate fav cache so next read reflects new state
            try { sessionStorage.removeItem(FAV_CACHE_KEY); } catch (e) {}

            const self = this;
            const url = '/api/favorites/' + encodeURIComponent(id);
            const opts = wasSaved
                ? { method: 'DELETE', credentials: 'same-origin' }
                : {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        property_id: id,
                        name: this._propertyName,
                        image: this._propertyImage,
                        address: this._propertyAddress,
                        url: this._propertyUrl,
                    }),
                };
            try {
                const resp = await fetch(url, opts);
                // 401 means session expired — this is the only "expected" error;
                // user-menu's storage/pageshow listeners will eventually catch
                // the auth change. Roll back local state to keep things in sync.
                if (resp.status === 401) {
                    self._applyFilledState(wasSaved);
                    writeLocal(wasSaved);
                    document.dispatchEvent(new CustomEvent('open-signin', { bubbles: true }));
                    return;
                }
                // Any other non-2xx: roll back optimistic UI + local state.
                if (!resp.ok) {
                    console.error('save-button: ' + opts.method + ' returned status ' + resp.status);
                    self._applyFilledState(wasSaved);
                    writeLocal(wasSaved);
                }
            } catch (err) {
                console.error('save-button: ' + opts.method + ' failed', err);
                self._applyFilledState(wasSaved);
                writeLocal(wasSaved);
            }
        }

    }

    if (!customElements.get('save-button')) {
        customElements.define('save-button', SaveButton);
    }

    // PR-perf.2: module-level user-changed listener replaces per-instance
    // pattern. user-changed fires only on auth state transitions
    // (login/logout) — rare. querySelectorAll walks live save-buttons
    // and re-evaluates filled state + merges server-side favorites.
    document.addEventListener('user-changed', function () {
        const els = document.querySelectorAll('save-button');
        for (let i = 0; i < els.length; i++) {
            const el = els[i];
            if (el._applyFilledState && el._isLocallySaved) {
                el._applyFilledState(el._isLocallySaved());
            }
            if (window.currentUser && el._mergeServerState) {
                el._mergeServerState();
            }
        }
    });
})();
